/**
 * Permission and module keys (ORCH-334), and each model's form permissions (ORCH-366)
 *
 * GENERATED by src/scripts/generate_frontend_permission_keys.py from
 * src/shared_core/shared_core/authz/permissions.py (the enums) and
 * src/shared_core/shared_core/authz/catalog.py (MODEL_ACCESS). DO NOT EDIT BY HAND:
 * change the Python, rerun the script and commit both. CI fails on drift.
 *
 * Gate with these members (`can(PermissionKey.MEDIAPLAN_EDIT)`,
 * `hasModule(ModuleKey.ADMIN_MDM_MANAGER)`), never with the string values.
 * Module keys never appear in `user.permissions`; check them with `hasModule`.
 *
 * Read MODEL_ACCESS through `resolvePickerPermission` (utils/pickerPermission.ts),
 * not directly.
 */

/** Data and functional permissions (ORCH-21 §8.1–§8.3). */
export enum PermissionKey {
  // --- Media plans (scoped) ------------------------------------------------
  /**
   * Permits: list and open plans inside the grant's scope (planning `/v1/mediaplan`
   * GET, internal plan reads); read a plan's files, on that plan's scope, through the
   * planning file proxy and core-api `/v1/files` (ORCH-332). Also the ancestor view a
   * plan file write needs. Rejects: any change to a plan or its files.
   */
  MEDIAPLAN_VIEW = 'mediaplan.view',
  /**
   * Permits: create plans; create-plan drafts. Rejects: editing existing plans.
   * From ORCH-332, every tag must also lie inside the grants that would make the
   * plan visible (§4 Create).
   */
  MEDIAPLAN_CREATE = 'mediaplan.create',
  /**
   * Permits: change plans. Rejects: delete, submit, approve, archive. From
   * ORCH-332, the plan and every tag must also stay inside the editor's scope
   * (§4 Edit).
   */
  MEDIAPLAN_EDIT = 'mediaplan.edit',
  /**
   * Permits: delete plans (planning DELETE `/v1/mediaplan/{id}`). Seeded for
   * system_admin, account_admin and planning_lead; planners don't hold it.
   */
  MEDIAPLAN_DELETE = 'mediaplan.delete',
  /** Reserved: send a plan for approval. No route checks it yet. */
  MEDIAPLAN_SUBMIT = 'mediaplan.submit',
  /**
   * Reserved: approve plans. No route checks it yet; workflow assignment rules
   * are added on top when ORCH-156 lands.
   */
  MEDIAPLAN_APPROVE = 'mediaplan.approve',
  /** Reserved: archive plans. No route checks it yet. */
  MEDIAPLAN_ARCHIVE = 'mediaplan.archive',
  /**
   * Reserved: export plans (rows to be limited by `MEDIAPLAN_VIEW` scope). No
   * route checks it yet.
   */
  MEDIAPLAN_EXPORT = 'mediaplan.export',
  /**
   * Permits: upload, rename and delete a plan's attachments when its scope covers
   * the plan's tags and `MEDIAPLAN_VIEW` covers the plan (planning file proxy writes
   * and core-api `/v1/files` writes on plan files, ORCH-332). Rejects: downloading,
   * which needs `MEDIAPLAN_VIEW`.
   */
  MEDIAPLAN_FILES = 'mediaplan.files',
  /**
   * Permits: Maestro on plans - chat, runs, the flights timeline, and the
   * run-state routes llm-service calls for the user. Rejects: plan actions
   * themselves, which Maestro performs with the user's own plan permissions.
   */
  MEDIAPLAN_USE_AI = 'mediaplan.use_ai',

  // --- Flights and line items (scoped; reserved) ----------------------------
  // Enforced when flights and line items become tables; they take their plan's
  // tags (§3.11). Seeded to follow each role's plan view and edit (§9.3).
  /** Permits: view flights in plans in scope (reserved). */
  FLIGHT_VIEW = 'flight.view',
  /** Permits: add flights to plans in scope (reserved). */
  FLIGHT_CREATE = 'flight.create',
  /** Permits: change flights in plans in scope (reserved). */
  FLIGHT_EDIT = 'flight.edit',
  /** Permits: delete flights in plans in scope (reserved). */
  FLIGHT_DELETE = 'flight.delete',
  /** Permits: view line items in plans in scope (reserved). */
  LINEITEM_VIEW = 'lineitem.view',
  /** Permits: add line items to plans in scope (reserved). */
  LINEITEM_CREATE = 'lineitem.create',
  /** Permits: change line items in plans in scope (reserved). */
  LINEITEM_EDIT = 'lineitem.edit',
  /** Permits: delete line items in plans in scope (reserved). */
  LINEITEM_DELETE = 'lineitem.delete',

  // --- Users: tenant memberships (scoped by the membership's security attributes)
  /**
   * Scoped. Permits: list, search and open the active tenant's users
   * inside the caller's scope (core-api `/users` reads). Rejects: the audit
   * log, which needs `AUDIT_VIEW`.
   */
  USER_VIEW = 'user.view',
  /**
   * Scoped. Permits: invite people into the active tenant, which also adds
   * an existing account to it; change, resend and cancel invitations that
   * haven't been accepted yet (ORCH-420). Rejects: setting roles (needs
   * `USER_ASSIGN_ROLES`) and adding people to another tenant (needs the
   * system_admin role in GLOBAL).
   */
  USER_CREATE = 'user.create',
  /**
   * Scoped. Permits: change a membership's organization and security
   * attributes in the active tenant, and change, resend and cancel invitations
   * that haven't been accepted yet (ORCH-420). Rejects: roles
   * (`USER_ASSIGN_ROLES`), status and removal (`USER_DEACTIVATE`), and the
   * person's own profile.
   */
  USER_EDIT = 'user.edit',
  /**
   * Scoped. Permits: deactivate, reactivate and remove a membership in the
   * active tenant, singly or in bulk. Changing the account in every tenant
   * also needs the system_admin role in GLOBAL.
   */
  USER_DEACTIVATE = 'user.deactivate',
  /**
   * Scoped. Permits: set a membership's roles, within the assignment guard
   * rails: a role's administrative permissions must fit inside the caller's
   * own, a system role needs a holder, and nobody changes their own roles.
   */
  USER_ASSIGN_ROLES = 'user.assign_roles',

  // --- Tenants (unscoped) ---------------------------------------------------
  /**
   * Baseline. Permits: read the member's own tenants. Rejects: reading or
   * listing other tenants, which needs `ModuleKey.ADMIN_TENANT_MANAGER`.
   */
  TENANT_VIEW = 'tenant.view',
  /** GLOBAL-only. Permits: create tenants. */
  TENANT_CREATE = 'tenant.create',
  /**
   * GLOBAL-only. Permits: change tenants and their feature toggles; reaching
   * another tenant's files for support (ORCH-331 decision 1). Rejects: tenant
   * members, which are `USER_*`.
   */
  TENANT_EDIT = 'tenant.edit',
  /** GLOBAL-only. Permits: delete tenants (never GLOBAL). */
  TENANT_DELETE = 'tenant.delete',

  // --- Configuration (unscoped) ---------------------------------------------
  /**
   * Baseline. Permits: read models, field definitions, fields and views in
   * the active tenant and GLOBAL. Rejects: other tenants' configuration.
   */
  METADATA_VIEW = 'metadata.view',
  /**
   * GLOBAL-only. Permits: change models, field definitions, fields and views,
   * including a tenant's own copies by acting for it from GLOBAL, and reading
   * any tenant's configuration (ORCH-331 decision 1).
   */
  METADATA_EDIT = 'metadata.edit',
  /** Baseline. Permits: read workflow definitions and steps. */
  WORKFLOW_VIEW = 'workflow.view',
  /**
   * GLOBAL-only. Permits: change workflow definitions and steps, and act for
   * or read any tenant's workflows.
   */
  WORKFLOW_EDIT = 'workflow.edit',
  /** Baseline. Permits: read AI agents (so Maestro can run them as the user). */
  AI_AGENT_VIEW = 'ai_agent.view',
  /**
   * GLOBAL-only. Permits: create, change, delete and revert AI agents, and act
   * for or read any tenant's agents.
   */
  AI_AGENT_EDIT = 'ai_agent.edit',
  /** Baseline. Permits: read AI skills. */
  AI_SKILL_VIEW = 'ai_skill.view',
  /** GLOBAL-only. Permits: create, change and delete AI skills (platform-wide). */
  AI_SKILL_EDIT = 'ai_skill.edit',

  // --- Master data ------------------------------------------------------------
  /**
   * Scoped. Reserved on the backend: no route checks it yet. Every master-data
   * read (lists, search, lookups, context) is open to every active member until
   * ORCH-332 scopes values by it and by the permission being exercised.
   */
  MDM_VIEW = 'mdm.view',
  /**
   * Scoped. Permits: create, change, delete, reactivate and revert values in
   * the active tenant; editing a GLOBAL value makes the tenant's own copy.
   * Rejects: writing GLOBAL or another tenant's values directly, which needs this
   * key held in GLOBAL.
   */
  MDM_EDIT = 'mdm.edit',
  /**
   * Scoped. Permits: add a missing value from a record's picker where the
   * field's `creation_access` is `User`. Rejects: `Admin`-access fields and
   * confirming an override of a shared value (both need `MDM_EDIT`).
   */
  MDM_INLINE_CREATE = 'mdm.inline_create',
  /**
   * Permits: GLOBAL aliases, when held in GLOBAL. Rejects nothing for tenant
   * aliases, which any member may attach to visible values (ORCH-115).
   */
  MDM_MANAGE_ALIASES = 'mdm.manage_aliases',
  /** Permits: bulk import into the active tenant, and its import batches. */
  MDM_IMPORT = 'mdm.import',
  /** Permits: export master data readable in the active tenant. */
  MDM_EXPORT = 'mdm.export',

  // --- Roles and audit (unscoped) -------------------------------------------
  /** Permits: list roles and their grants (core-api `/authz/roles`). */
  ROLE_VIEW = 'role.view',
  /** Permits: create roles (Role Manager, ORCH-333). */
  ROLE_CREATE = 'role.create',
  /** Permits: change roles (Role Manager, ORCH-333). */
  ROLE_EDIT = 'role.edit',
  /** Permits: delete roles (Role Manager, ORCH-333). */
  ROLE_DELETE = 'role.delete',
  /**
   * Permits: read the active tenant's activity log (core-api
   * `/users/activity-logs`).
   */
  AUDIT_VIEW = 'audit.view',

  // --- Functional ---------------------------------------------------------------
  /**
   * Reserved: read-only reports combined across the tenants the user belongs
   * to (§3.10, ORCH-338). No code checks it yet.
   */
  REPORTING_CROSS_TENANT = 'reporting.cross_tenant',
}

/**
 * Areas of the application, each held Hidden, Read-only or Editable (§3.12).
 *
 * Modules gate navigation and module-only screens; data endpoints check
 * `PermissionKey`s, never modules (§3.12 rule 4). A level's included
 * permissions are listed in `catalog.py`.
 */
export enum ModuleKey {
  /**
   * Planning. Read-only includes plan view; Editable adds create, edit, submit,
   * files and export.
   */
  PLANNING = 'module.planning',
  /** Maestro, under Planning. Editable includes `MEDIAPLAN_USE_AI`. */
  PLANNING_MAESTRO = 'module.planning.maestro',
  /** Analytics (Read-only only). */
  ANALYTICS = 'module.analytics',
  /**
   * The Admin Center. Derived, never chosen: held at Read-only iff any manager
   * below is held (ORCH-415).
   */
  ADMIN = 'module.admin',
  /** User Management. Depends on Role Manager Read-only. */
  ADMIN_USER_MANAGER = 'module.admin.user_manager',
  /** Role Manager. */
  ADMIN_ROLE_MANAGER = 'module.admin.role_manager',
  /** Master Data Manager. */
  ADMIN_MDM_MANAGER = 'module.admin.mdm_manager',
  /** Metadata Manager. Editable is GLOBAL-only. */
  ADMIN_METADATA_MANAGER = 'module.admin.metadata_manager',
  /** Workflow Manager. Editable is GLOBAL-only. */
  ADMIN_WORKFLOW_MANAGER = 'module.admin.workflow_manager',
  /** AI Agent Manager. Editable is GLOBAL-only. */
  ADMIN_AI_AGENT_MANAGER = 'module.admin.ai_agent_manager',
  /** AI Skills Manager. Editable is GLOBAL-only. */
  ADMIN_AI_SKILLS_MANAGER = 'module.admin.ai_skills_manager',
  /**
   * Tenant Manager. GLOBAL-only. Holding it lists and reads every tenant
   * (core-api `/tenants`).
   */
  ADMIN_TENANT_MANAGER = 'module.admin.tenant_manager',
}

// ---------------------------------------------------------------------------
// Which permissions a model's forms exercise (ORCH-366), from catalog.MODEL_ACCESS
// ---------------------------------------------------------------------------

export interface ModelAccess {
  view: PermissionKey;
  create: PermissionKey | null;
  edit: PermissionKey | null;
}

/** Normal models, by `models.model_id`. */
export const MODEL_ACCESS: Readonly<Record<string, ModelAccess>> = {
  mediaplan: {
    view: PermissionKey.MEDIAPLAN_VIEW,
    create: PermissionKey.MEDIAPLAN_CREATE,
    edit: PermissionKey.MEDIAPLAN_EDIT,
  },
  flight: {
    view: PermissionKey.FLIGHT_VIEW,
    create: PermissionKey.FLIGHT_CREATE,
    edit: PermissionKey.FLIGHT_EDIT,
  },
  lineitem: {
    view: PermissionKey.LINEITEM_VIEW,
    create: PermissionKey.LINEITEM_CREATE,
    edit: PermissionKey.LINEITEM_EDIT,
  },
  user_tenant: {
    view: PermissionKey.USER_VIEW,
    create: PermissionKey.USER_CREATE,
    edit: PermissionKey.USER_EDIT,
  },
  tenant: {
    view: PermissionKey.TENANT_VIEW,
    create: PermissionKey.TENANT_CREATE,
    edit: PermissionKey.TENANT_EDIT,
  },
};

/** Every fielddef model (`model_id` is its `fd_*` field definition id). */
export const FIELDDEF_MODEL_ACCESS: ModelAccess = {
  view: PermissionKey.MDM_VIEW,
  create: PermissionKey.MDM_EDIT,
  edit: PermissionKey.MDM_EDIT,
};

/** Normal models whose forms are deliberately unscoped (reasons in the catalog). */
export const UNSCOPED_MODELS: readonly string[] = ['user'];
